import { trackEvent } from '@/lib/telemetry';
// BSX-export (BrickStore XML) - het uitwisselformaat van BrickStore/BrickStock.
// Vereist BrickLink part- en kleur-ID's (bl_part_id/bl_color_id); parts zonder
// mapping worden overgeslagen en als XML-comment in het bestand vermeld.

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// parts: array van API-part-objecten ({ bl_part_id, bl_color_id, quantity, partId }).
// remarks komt per item in het Remarks-veld (bijv. "Bag 3"), status is optioneel
// ("E" voor extra parts). Retourneert { items, skipped }.
export function partsToBsxItems(parts, { remarks, status } = {}) {
  const items = [];
  const skipped = [];
  for (const part of parts) {
    if (!part.bl_part_id || part.bl_color_id === null || part.bl_color_id === undefined) {
      skipped.push(part.partId);
      continue;
    }
    items.push({
      blPartId: part.bl_part_id,
      blColorId: Number(part.bl_color_id),
      qty: part.quantity,
      remarks,
      status,
    });
  }
  return { items, skipped };
}

export function buildBsx(items, skipped = []) {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!DOCTYPE BrickStoreXML>',
    '<BrickStoreXML>',
    ' <Inventory>',
  ];
  if (skipped.length > 0) {
    const unique = [...new Set(skipped)];
    lines.push(`  <!-- ${unique.length} part(s) not included (no BrickLink mapping yet): ${escapeXml(unique.join(', '))} -->`);
  }
  for (const item of items) {
    lines.push('  <Item>');
    lines.push(`   <ItemID>${escapeXml(item.blPartId)}</ItemID>`);
    lines.push('   <ItemTypeID>P</ItemTypeID>');
    lines.push(`   <ColorID>${item.blColorId}</ColorID>`);
    lines.push(`   <Qty>${item.qty}</Qty>`);
    lines.push('   <Condition>N</Condition>');
    if (item.status) lines.push(`   <Status>${item.status}</Status>`);
    if (item.remarks) lines.push(`   <Remarks>${escapeXml(item.remarks)}</Remarks>`);
    lines.push('  </Item>');
  }
  lines.push(' </Inventory>');
  lines.push('</BrickStoreXML>');
  return lines.join('\n');
}

export function downloadBsx(filename, xml, mime = 'application/xml') {
  const blob = new Blob([xml], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const EXTRA_BAG_NUMBERS = new Set(['0', 'Extra', 'Extra parts']);

export function isExtraBag(bagNumber) {
  return EXTRA_BAG_NUMBERS.has(bagNumber);
}

export function bagRemarksLabel(bag) {
  if (isExtraBag(bag.bagNumber)) return 'Extra parts';
  const word = bag.virtual ? 'Group' : 'Bag';
  const numbers = bag.displayNumbers?.length ? bag.displayNumbers.join('+') : bag.bagNumber;
  return `${word} ${numbers}`;
}

// Eén bag als .bsx downloaden
export function downloadBagBsx(setId, bag) {
  const { items, skipped } = partsToBsxItems(bag.parts, {
    remarks: `LEGO ${setId} - ${bagRemarksLabel(bag)}`,
    status: isExtraBag(bag.bagNumber) ? 'E' : undefined,
  });
  const suffix = isExtraBag(bag.bagNumber) ? 'extra-parts' : `bag-${String(bag.bagNumber).replace(/[^\w-]+/g, '_')}`;
  downloadBsx(`lego-${setId}-${suffix}.bsx`, buildBsx(items, skipped));
  trackEvent('Set Downloaded', { set: String(setId), format: 'bsx', scope: 'bag' });
}

// Hele set als .bsx downloaden, met bag-nummer in Remarks per item
export function downloadSetBsx(setId, bags) {
  const items = [];
  const skipped = [];
  for (const bag of bags) {
    const result = partsToBsxItems(bag.parts, {
      remarks: bagRemarksLabel(bag),
      status: isExtraBag(bag.bagNumber) ? 'E' : undefined,
    });
    items.push(...result.items);
    skipped.push(...result.skipped);
  }
  downloadBsx(`lego-${setId}.bsx`, buildBsx(items, skipped));
  trackEvent('Set Downloaded', { set: String(setId), format: 'bsx', scope: 'set' });
}
